import React, { useState, useEffect, useCallback } from 'react';
import PlayerNameButton from '../../../components/playerSheet/PlayerNameButton';
import { API_URLS } from '../../../config/Constants';
import api from '../../../config/axiosConfig';
import { sessionManager } from '../../../config/sessionManager';
import { showErrorMsg } from '../../../config/common';

// Player types for each section
interface PlayerBase {
  playerNumber: string;
  playerName: string;
  playerId: string;
}

interface PossessionPlayer extends PlayerBase {
  groundBalls: number;
  turnovers: number;
  causedTurnovers: number;
  shotsSaved: number;
  npc: number;
}

interface OffensePlayer extends PlayerBase {
  shots: number;
  saved: number;
  goals: number;
  assists: number;
  points: number;
  shotPercentage: number;
  sogPercentage: number;
}

interface GoaliePlayer extends PlayerBase {
  shotsOnGoal: number;
  saved: number;
  goalsAllowed: number;
  savePercentage: number;
}

interface FaceoffPlayer extends PlayerBase {
  faceoffs: number;
  won: number;
  lost: number;
  winPercentage: number;
  lossPercentage: number;
}

interface PenaltyPlayer extends PlayerBase {
  penalties: number;
}

// Table component
function StatsTable<T extends PlayerBase>({
  title,
  columns,
  players,
  sortConfig,
  onSortChange,
}: {
  title: string;
  columns: {
    header: string;
    key: keyof T | 'special';
    render?: (player: T) => React.ReactNode;
    sortable?: boolean;
  }[];
  players: T[];
  sortConfig?: {
    key: string;
    direction: 'asc' | 'desc';
  } | null;
  onSortChange?: (sortConfig: { key: string; direction: 'asc' | 'desc' } | null) => void;
}) {
  const toSortableNumber = (value: unknown): number | null => {
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
    }

    if (typeof value !== 'string') return null;

    const cleaned = value.trim().replace(/,/g, '').replace(/%$/, '');
    if (cleaned === '') return null;

    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  };

  // Sort players based on current sort configuration
  const sortedPlayers = React.useMemo(() => {
    if (!sortConfig) return players;

    return [...players].sort((a, b) => {
      const { key, direction } = sortConfig;
      let aValue: any = a[key as keyof T];
      let bValue: any = b[key as keyof T];

      // Compare numeric values (supports numbers and numeric-like strings, including percents)
      const aNumber = toSortableNumber(aValue);
      const bNumber = toSortableNumber(bValue);
      if (aNumber !== null && bNumber !== null) {
        return direction === 'asc' ? aNumber - bNumber : bNumber - aNumber;
      }

      // String comparison
      const aStr = String(aValue || '').toLowerCase();
      const bStr = String(bValue || '').toLowerCase();
      if (aStr < bStr) return direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [players, sortConfig]);

  const handleSort = (key: keyof T | 'special', idx: number) => {
    const column = columns[idx];
    if (!column || column.sortable === false) return;

    if (onSortChange) {
      let direction: 'asc' | 'desc' = 'desc'; // Default to desc for stats
      if (sortConfig?.key === String(key) && sortConfig.direction === 'desc') {
        direction = 'asc';
      }
      onSortChange({ key: String(key), direction });
    }
  };

  const getSortIcon = (key: keyof T | 'special', idx: number) => {
    const column = columns[idx];
    if (!column || column.sortable === false) return null;

    const sortKey = String(key);
    if (sortConfig?.key === sortKey) {
      return sortConfig.direction === 'asc' ? <span className="ml-1">↑</span> : <span className="ml-1">↓</span>;
    }
    return <span className="ml-1 opacity-50">↕</span>;
  };
  return (
    <div className="mb-6">
      <div className="bg-[#05396b] text-white text-xl font-bold px-6 py-3">{title}</div>
      <div className="overflow-x-auto shadow-xl">
        <table className="text-center border border-[#22c55e] table-fixed w-full">
          <colgroup>
            <col style={{ width: '80px' }} />
            <col style={{ width: '180px' }} />
            {columns.slice(2).map((_, idx) => (
              <col key={idx} style={{ width: '120px' }} />
            ))}
          </colgroup>
          <thead>
            <tr className="bg-[#05396b] text-white">
              {columns.map((col, idx) => (
                <th
                  key={idx}
                  className={`px-0.5 py-0.5 border border-[#22c55e] text-xs lg:text-sm font-semibold ${
                    col.sortable !== false ? 'cursor-pointer hover:bg-blue-700 transition-colors' : ''
                  }`}
                  onClick={() => (col.sortable !== false ? handleSort(col.key, idx) : undefined)}
                >
                  <div className="flex items-center justify-center">
                    {col.header}
                    {col.sortable !== false && getSortIcon(col.key, idx)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="text-xs sm:text-sm bg-[#f6fff0]">
            {sortedPlayers.map((player, idx) => (
              <tr key={player.playerId} className={idx % 2 === 1 ? 'bg-[#e6fce0]' : 'bg-white'}>
                {columns.map((col, colIdx) => {
                  const cellContent = col.render
                    ? col.render(player)
                    : String(player[col.key as keyof T]);
                  const isNameCol = col.key === 'playerName';
                  const seasonId = sessionManager.getSeasonId();
                  return (
                    <td key={colIdx} className="px-0.5 py-1 border border-gray-400 text-center">
                      {isNameCol ? (
                        <PlayerNameButton playerSeasonId={player.playerId} seasonId={seasonId}>
                          {cellContent}
                        </PlayerNameButton>
                      ) : (
                        cellContent
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Quarter Selector Component
const QuarterSelector: React.FC<{
  periods: string[];
  activeQuarter: string;
  onQuarterChange: (quarter: string) => void;
}> = ({ periods, activeQuarter, onQuarterChange }) => (
  <div className="sticky top-0 z-30 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-3 mb-6 flex gap-2 bg-white border-b border-[#bccedf]">
    {periods.map((q) => (
      <button
        key={q}
        className={`px-4 py-2 rounded font-semibold transition-colors ${
          activeQuarter === q ? 'bg-[#05396b] text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        }`}
        onClick={() => onQuarterChange(q)}
      >
        {q}
      </button>
    ))}
  </div>
);

// Main Component
const PlayersPageWeb: React.FC = () => {
  const clientId = sessionManager.getClientId();
  const seasonId = sessionManager.getSeasonId();
  const gameId = sessionManager.getGameId();

  const [periods, setPeriods] = useState<string[]>([]);
  const [activeQuarter, setActiveQuarter] = useState('Totals');

  // Section data
  const [possessionPlayers, setPossessionPlayers] = useState<PossessionPlayer[]>([]);
  const [offensePlayers, setOffensePlayers] = useState<OffensePlayer[]>([]);
  const [goaliePlayers, setGoaliePlayers] = useState<GoaliePlayer[]>([]);
  const [faceoffPlayers, setFaceoffPlayers] = useState<FaceoffPlayer[]>([]);
  const [penaltyPlayers, setPenaltyPlayers] = useState<PenaltyPlayer[]>([]);

  // Sort configurations for each table
  const [possessionSortConfig, setPossessionSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [offenseSortConfig, setOffenseSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [goalieSortConfig, setGoalieSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [faceoffSortConfig, setFaceoffSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });
  const [penaltySortConfig, setPenaltySortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({ key: 'playerNumber', direction: 'asc' });

  // Column definitions
  const possessionColumns = [
    {
      header: 'Player #',
      key: 'playerNumber' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'Player Name',
      key: 'playerName' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'Ground Balls',
      key: 'groundBalls' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'Turnovers',
      key: 'turnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'Caused Turnovers',
      key: 'causedTurnovers' as keyof PossessionPlayer,
      sortable: true,
    },
    {
      header: 'Shots Saved',
      key: 'shotsSaved' as keyof PossessionPlayer,
      sortable: true,
    },
    { header: 'NPC', key: 'npc' as keyof PossessionPlayer, sortable: true },
  ];

  const offenseColumns = [
    {
      header: 'Player #',
      key: 'playerNumber' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'Player Name',
      key: 'playerName' as keyof OffensePlayer,
      sortable: true,
    },
    { header: 'Shots', key: 'shots' as keyof OffensePlayer, sortable: true },
    { header: 'Saved', key: 'saved' as keyof OffensePlayer, sortable: true },
    {
      header: 'Goals',
      key: 'goals' as keyof OffensePlayer,
      sortable: true,
    },
    {
      header: 'Shot%',
      key: 'shotPercentage' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => <div className="text-sm">{player.shotPercentage.toFixed(0)}%</div>,
    },
    {
      header: 'SOG%',
      key: 'sogPercentage' as keyof OffensePlayer,
      sortable: true,
      render: (player: OffensePlayer) => <div className="text-sm">{player.sogPercentage}%</div>,
    },
    {
      header: 'Assists',
      key: 'assists' as keyof OffensePlayer,
      sortable: true,
    },
    { header: 'Points', key: 'points' as keyof OffensePlayer, sortable: true },
  ];

  const goalieColumns = [
    {
      header: 'Player #',
      key: 'playerNumber' as keyof GoaliePlayer,
      sortable: true,
    },
    {
      header: 'Player Name',
      key: 'playerName' as keyof GoaliePlayer,
      sortable: true,
    },
    {
      header: 'Shots on Goal',
      key: 'shotsOnGoal' as keyof GoaliePlayer,
      sortable: true,
    },
    { header: 'Saves', key: 'saved' as keyof GoaliePlayer, sortable: true },
    {
      header: 'SV%',
      key: 'savePercentage' as keyof GoaliePlayer,
      sortable: true,
      render: (player: GoaliePlayer) => <div className="text-sm">{player.savePercentage.toFixed(0)}%</div>,
    },
    {
      header: 'Goals Allowed',
      key: 'goalsAllowed' as keyof GoaliePlayer,
      sortable: true,
    },
  ];

  const faceoffColumns = [
    {
      header: 'Player #',
      key: 'playerNumber' as keyof FaceoffPlayer,
      sortable: true,
    },
    {
      header: 'Player Name',
      key: 'playerName' as keyof FaceoffPlayer,
      sortable: true,
    },
    {
      header: 'Face-offs',
      key: 'faceoffs' as keyof FaceoffPlayer,
      sortable: true,
    },
    { header: 'Won', key: 'won' as keyof FaceoffPlayer, sortable: true },
    {
      header: 'W%',
      key: 'winPercentage' as keyof FaceoffPlayer,
      sortable: true,
      render: (player: FaceoffPlayer) => <div className="text-sm">{player.winPercentage.toFixed(0)}%</div>,
    },
    { header: 'Lost', key: 'lost' as keyof FaceoffPlayer, sortable: true },
  ];

  const penaltyColumns = [
    {
      header: 'Player #',
      key: 'playerNumber' as keyof PenaltyPlayer,
      sortable: true,
    },
    {
      header: 'Player Name',
      key: 'playerName' as keyof PenaltyPlayer,
      sortable: true,
    },
    {
      header: 'Penalties',
      key: 'penalties' as keyof PenaltyPlayer,
      sortable: true,
    },
  ];

  // Fetch periods on component mount and when gameId changes (same API as mobile)
  useEffect(() => {
    fetchPeriods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId]);

  // Fetch player stats when quarter changes
  useEffect(() => {
    if (periods.length > 0) {
      fetchPlayerStats();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeQuarter, periods]);

  const fetchPeriods = async () => {
    try {
      if (!clientId || !seasonId || !gameId) return;

      const response = await api.get(`${API_URLS.GET_GAME_PERIODS}/${gameId}/periods`, {
        params: { clientId, seasonId },
      });

      if (response.data.success) {
        const data = response.data.data;
        const hp = data.halfPeriod;
        const otFlag = data.otFlag;
        let periodArr: string[] = [];

        if (hp === true) {
          periodArr = ['Totals', 'H1', 'H2'];
        } else {
          periodArr = ['Totals', 'Q1', 'Q2', 'Q3', 'Q4'];
        }

        if (otFlag && !periodArr.includes('OT')) {
          periodArr.push('OT');
        }

        setPeriods(periodArr);
        setActiveQuarter('Totals');
      }
    } catch (error) {
      console.error('Failed to fetch periods:', error);
      setPeriods(['Totals', 'Q1', 'Q2', 'Q3', 'Q4']);
      setActiveQuarter('Totals');
    }
  };

  const fetchPlayerStats = useCallback(async () => {
    if (!clientId || !seasonId || !activeQuarter) return;

    try {
      let url = `${API_URLS.GET_PLAYER_STATS}?clientId=${clientId}&seasonId=${seasonId}&quarterId=${activeQuarter}`;
      if (gameId) {
        url += `&gameId=${gameId}`;
      }

      const response = await api.get(url);

      if (response.data.success) {
        const playersData = response.data.data;

        // Process and organize data by sections
        processPossessionData(playersData);
        processOffenseData(playersData);
        processGoalieData(playersData);
        processFaceoffData(playersData);
        processPenaltyData(playersData);
      } else {
        showErrorMsg(response.data.message || 'Failed to fetch player statistics');
      }
    } catch (err: any) {
      showErrorMsg(err.response?.data?.message || err.message || 'Failed to fetch player statistics');
    }
  }, [clientId, seasonId, activeQuarter, gameId]);

  const processPossessionData = (playersData: any[]) => {
    const processed: PossessionPlayer[] = playersData
      .map((player) => {
        const groundBalls = player.groundballs || 0;
        const turnovers = player.turnovers || 0;
        const causedTurnovers = player.causedturnover || 0;
        const shotsSaved = player.shotsaved || 0;
        const position = player.position || [];

        // NPC Definition: NPC = Ground Balls - Net Turnovers (TO - CTOs) - Shots Saved
        // Net Turnovers = Turnovers - Caused Turnovers
        const netTurnovers = turnovers - causedTurnovers;
        const npc = groundBalls - netTurnovers - shotsSaved;
        const activity = groundBalls + turnovers + causedTurnovers + shotsSaved;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          groundBalls,
          turnovers,
          causedTurnovers,
          shotsSaved,
          activity,
          npc,
          position,
        };
      })
      .filter((player) => player.activity > 0);

    setPossessionPlayers(processed);
  };

  const processOffenseData = (playersData: any[]) => {
    const processed: OffensePlayer[] = playersData
      .map((player) => {
        const shots = player.shots || 0;
        const saved = player.shotsaved || 0;
        const goals = player.goals || 0;
        const assists = player.assist || 0;
        const points = goals + assists;
        const shotPercentage = shots > 0 ? Number(((goals / shots) * 100).toFixed(0)) : 0;
        const sogPercentage = shots > 0 ? Math.ceil(((goals + saved) / shots) * 100) : 0;
        const position = player.position || [];
        const activity = shots + saved + goals + assists;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          shots,
          saved,
          goals,
          assists,
          points,
          shotPercentage,
          sogPercentage,
          activity,
          position,
        };
      })
      .filter((player) => player.activity > 0);

    setOffensePlayers(processed);
  };

  const processGoalieData = (playersData: any[]) => {
    const processed: GoaliePlayer[] = playersData
      .filter((player) => player.shotsongoal)
      .map((player) => {
        const shotsOnGoal = player.shotsongoal || 0;
        const saved = player.saved || 0;
        const goalsAllowed = player.goalsallowed || 0;
        const savePercentage = shotsOnGoal > 0 ? Number(((saved / shotsOnGoal) * 100).toFixed(0)) : 0;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName?.[0] || ''}.`,
          shotsOnGoal,
          saved,
          goalsAllowed,
          savePercentage,
        };
      });

    setGoaliePlayers(processed);
  };

  const processFaceoffData = (playersData: any[]) => {
    const processed: FaceoffPlayer[] = playersData
      .filter((player) => (player.faceoffwins || 0) > 0 || (player.faceofflosses || 0) > 0)
      .map((player) => {
        const won = player.faceoffwins || 0;
        const lost = player.faceofflosses || 0;
        const total = won + lost;
        const winPercentage = total > 0 ? Number(((won / total) * 100).toFixed(0)) : 0;
        const lossPercentage = total > 0 ? Number(((lost / total) * 100).toFixed(0)) : 0;

        return {
          playerId: player.playerSeasonId || player._id,
          playerNumber: player.no,
          playerName: `${player.firstName} ${player.lastName}`,
          faceoffs: total,
          won,
          lost,
          winPercentage,
          lossPercentage,
        };
      });

    setFaceoffPlayers(processed);
  };

  const processPenaltyData = (playersData: any[]) => {
    const processed: PenaltyPlayer[] = playersData
      .filter((player) => (player.penalties || 0) > 0)
      .map((player) => ({
        playerId: player.playerSeasonId || player._id,
        playerNumber: player.no,
        playerName: `${player.firstName} ${player.lastName}`,
        penalties: player.penalties || 0,
      }));

    setPenaltyPlayers(processed);
  };

  return (
    <div className="w-full mb-10 mt-6 px-4 sm:px-6 lg:px-8">
      <header className="mb-8 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#05396b]">Players Report</h1>
          <p className="mt-2 text-sm sm:text-base text-[#05396b] font-medium max-w-3xl">
            Individual player statistics by quarter (or totals). Use the refresh button to pull the latest numbers.
          </p>
        </div>
        <button
          className="bg-[#7fe526] w-10 h-10 rounded-md p-2 shadow-md cursor-pointer hover:bg-[#6fd315] transition-colors flex-shrink-0"
          onClick={fetchPlayerStats}
          title="Refresh Player Stats"
        >
          <img src="/icons/icon-refresh.png" alt="refresh" className="w-full h-full" />
        </button>
      </header>

      {/* Quarter Selector */}
      <QuarterSelector periods={periods} activeQuarter={activeQuarter} onQuarterChange={setActiveQuarter} />

      {/* Player Statistics Tables */}
      <div className="border border-[#bccedf] overflow-auto p-6 rounded-lg shadow-sm">
        <StatsTable
          title="Possession"
          columns={possessionColumns}
          players={possessionPlayers}
          sortConfig={possessionSortConfig}
          onSortChange={setPossessionSortConfig}
        />
        <StatsTable
          title="Offense"
          columns={offenseColumns}
          players={offensePlayers}
          sortConfig={offenseSortConfig}
          onSortChange={setOffenseSortConfig}
        />
        <StatsTable
          title="Goalie"
          columns={goalieColumns}
          players={goaliePlayers}
          sortConfig={goalieSortConfig}
          onSortChange={setGoalieSortConfig}
        />
        <StatsTable
          title="Face-offs"
          columns={faceoffColumns}
          players={faceoffPlayers}
          sortConfig={faceoffSortConfig}
          onSortChange={setFaceoffSortConfig}
        />
        <StatsTable
          title="Penalties"
          columns={penaltyColumns}
          players={penaltyPlayers}
          sortConfig={penaltySortConfig}
          onSortChange={setPenaltySortConfig}
        />
      </div>
    </div>
  );
};

export default PlayersPageWeb;
